import { DefaultButton, DialogFooter, PrimaryButton } from "@fluentui/react";
import { toast } from "components/FluentToast";
import ComboBoxField from "components/controls/ComboBoxField";
import DoubleNumberInputField from "components/controls/DoubleNumberInputField";
import GroupPolicyNumberField from "components/controls/GroupPolicyNumberField";
import NumberInputField from "components/controls/NumberInputField";
import TextInputField from "components/controls/TextInputField";
import { Formik } from "formik";
import { useCreateFleetInsurerDefaultSetting } from "hooks/data/mutations/useCreateFleetInsurerDefaultSetting";
import { useUpdateFleetInsurerDefaultSetting } from "hooks/data/mutations/useUpdateFleetInsurerDefaultSetting";
import { useGetFleetInsurerDefaultSettings } from "hooks/data/queries/getFleetInsurerDefaultSettings";
import { EActionDialog } from "pages/fleet-form/FleetForm/constants";
import useActionConfig from "pages/fleet-form/hooks/useActionConfg";
import { formatDayMonthValueForPayload } from "pages/vehicle/helpers";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { loadFleetWithInsurersActions } from "store/fleetForm";
import { useSelector } from "store/hooks";
import styled from "styled-components";
import { IFleetInsurerDefaultSetting, PremiumType } from "types/types";
import { stringToNumber } from "utils/utils";
import * as yup from "yup";
import { EDialogType } from "../constats";
import BranchOfficeField from "./BranchOfficeField";

type Props = {
  onClose: () => void;
  actionType: EDialogType | string;
  defaultSettings: IFleetInsurerDefaultSetting;
};

const suspensionDaysPremiumTypes = [
  PremiumType.MTPL,
  PremiumType.HULL,
  PremiumType.HULL_BASIC,
  PremiumType.LEGAL,
  PremiumType.ACCIDENT,
  PremiumType.ASSISTANCE,
  PremiumType.HORSE_POWER,
  PremiumType.FLEETLEGAL_PRORATA,
  PremiumType.HULL_ON_LAND_PRORATA,
  PremiumType.MACHINERY_BREAKDOWN_PRORATA,
];

const formatNumberForPayload = (value) => {
  if (value?.toString().trim() === "") return null;

  const number = Number(value);

  if (!isNaN(number)) return number;

  return null;
};

function InsurerSettingForm({ onClose, actionType, defaultSettings }: Props) {
  const { fleetId } = useParams() as any;
  const dispatch = useDispatch();

  const {
    mutate: onCreateFleetInsurerDefaultSetting,
    isLoading: createSettingInProgress,
  } = useCreateFleetInsurerDefaultSetting();

  const { mutate: onUpdateFleetInsurerDefaultSetting } =
    useUpdateFleetInsurerDefaultSetting();

  const { data: fleetInsurerDefaultSettings } =
    useGetFleetInsurerDefaultSettings(fleetId);

  const settingItemData = defaultSettings;

  const isEditSetting = actionType === EActionDialog.EditSetting;
  const isCreateSetting = actionType === EActionDialog.CreateSetting;
  const isCopySetting = actionType === EActionDialog.CopySettings;
  const isReadOnly = actionType === EActionDialog.ReadOnly;

  const { t } = useTranslation();
  const fleetAppSettings = useSelector((s) => s.fleetForm.fleetAppSettings);
  const taxonomy = useSelector((s) => s.taxonomy);

  const {
    paymentMeanOptions,
    paymentMethodOptions,
    insurerOptions,
    insurerSettingPremiumTypeOptions,
  } = useActionConfig(fleetAppSettings);

  // Remove the old mtplGroupPolicyInfo and hullGroupPolicyInfo usage
  // const mtplGroupPolicyInfo = useSelector(
  //   (s) => s.fleetForm.fleetWithPremiums.mtplGroupPolicyInfo
  // );
  // const hullGroupPolicyInfo = useSelector(
  //   (s) => s.fleetForm.fleetWithPremiums.hullGroupPolicyInfo
  // );

  // Replace with dynamic options based on selected insurer
  const getInsurerGroupPolicyNumberOptions = useCallback(
    (selectedInsurerValue: number | null) => {
      if (!selectedInsurerValue || !fleetInsurerDefaultSettings) {
        return [];
      }

      const retVal = new Set<any>();

      fleetInsurerDefaultSettings
        .filter(
          (setting) =>
            setting.insurerPartnerNumber === selectedInsurerValue &&
            setting.insurerGroupPolicyNumber !== null &&
            setting.insurerGroupPolicyNumber !== ""
        )
        .map((setting) => ({
          label: setting.insurerGroupPolicyNumber,
          value: setting.insurerGroupPolicyNumber,
        }))
        .forEach((item) => {
          if (!Array.from(retVal).find((it) => it.value === item.value)) {
            retVal.add(item);
          }
        });

      return Array.from(retVal);
    },
    [fleetInsurerDefaultSettings]
  );

  const initialValues = useMemo(() => {
    const showValues = isEditSetting || isCopySetting || isReadOnly;

    const formattedRenewalDay = settingItemData?.renewalDay
      ?.toString()
      .padStart(2, "0");
    const formattedRenewalMonth = settingItemData?.renewalMonth
      ?.toString()
      .padStart(2, "0");

    const suspensionDaysLimit = suspensionDaysPremiumTypes.map((pt) => {
      if (!showValues) return "";
      const premiumTypeId = taxonomy?.PremiumType?.byCode[pt]?.id;
      const detail =
        settingItemData?.fleetInsurerDefaultSettingPerPremiumTypeDetails?.find(
          (d) => d.premiumTypeCode === premiumTypeId
        );
      return detail?.suspensionDaysLimit != null
        ? detail.suspensionDaysLimit.toString()
        : "";
    });

    return {
      suspensionDaysLimit,
      insurerPartnerNumber: showValues
        ? insurerOptions.find(
            (insurer) => insurer.value === settingItemData?.insurerPartnerNumber
          )
        : null,
      suspensionDays: showValues
        ? settingItemData?.suspensionDays?.toString()
        : null,
      activationDays: showValues
        ? settingItemData?.activationDays?.toString()
        : null,
      garageRiskDiscountPercentHull: showValues
        ? settingItemData?.garageRiskDiscountPercentHull
            ?.toString()
            .replace(".", ",")
        : null,
      garageRiskDiscountPercentHullBasic: showValues
        ? settingItemData?.garageRiskDiscountPercentHullBasic
            ?.toString()
            .replace(".", ",")
        : null,
      insurerBranchOffice: showValues
        ? settingItemData?.insurerBranchOffice?.toString()
        : null,
      paymentMethod: showValues
        ? paymentMethodOptions.find(
            (paymentMethod) =>
              paymentMethod.value === settingItemData?.paymentMethod
          )
        : null,
      paymentMethodFleetLegal: showValues
        ? paymentMethodOptions.find(
            (paymentMethod) =>
              paymentMethod.value === settingItemData?.paymentMethodFleetLegal
          )
        : null,
      paymentMethodHullOnLand: showValues
        ? paymentMethodOptions.find(
            (paymentMethod) =>
              paymentMethod.value === settingItemData?.paymentMethodHullOnLand
          )
        : null,
      paymentMethodMachineryBreakdown: showValues
        ? paymentMethodOptions.find(
            (paymentMethod) =>
              paymentMethod.value ===
              settingItemData?.paymentMethodMachineryBreakdown
          )
        : null,
      paymentMethodEngineTax: showValues
        ? paymentMethodOptions.find(
            (paymentMethod) =>
              paymentMethod.value === settingItemData?.paymentMethodEngineTax
          )
        : null,
      paymentMean: showValues
        ? paymentMeanOptions.find(
            (paymentMean) => paymentMean.value === settingItemData?.paymentMean
          )
        : null,
      paymentMeanFleetLegal: showValues
        ? paymentMeanOptions.find(
            (paymentMean) =>
              paymentMean.value === settingItemData?.paymentMeanFleetLegal
          )
        : null,
      paymentMeanHullOnLand: showValues
        ? paymentMeanOptions.find(
            (paymentMean) =>
              paymentMean.value === settingItemData?.paymentMeanHullOnLand
          )
        : null,
      paymentMeanMachineryBreakdown: showValues
        ? paymentMeanOptions.find(
            (paymentMean) =>
              paymentMean.value ===
              settingItemData?.paymentMeanMachineryBreakdown
          )
        : null,

      ...(fleetAppSettings?.INCLUDE_INSURER_SETTING_PREMIUM_TYPE && {
        premiumTypeCode: showValues
          ? insurerSettingPremiumTypeOptions?.find(
              (opt) => opt.value === settingItemData?.premiumTypeCode
            ) ?? null
          : null,
      }),

      ...(fleetAppSettings?.INCLUDE_GROUP_POLICY && {
        insurerGroupPolicyNumber: showValues
          ? // Use the new dynamic options function to find the correct option
            getInsurerGroupPolicyNumberOptions(
              settingItemData?.insurerPartnerNumber
            ).find(
              (insurerGroupPolicyNumber: any) =>
                insurerGroupPolicyNumber?.value ===
                settingItemData?.insurerGroupPolicyNumber
            )
          : null,
        renewalDate:
          showValues && formattedRenewalDay && formattedRenewalMonth
            ? `${formattedRenewalDay}/${formattedRenewalMonth}`
            : null,
      }),
    };
  }, [actionType, getInsurerGroupPolicyNumberOptions, taxonomy]);

  const validationSchema = useMemo(() => {
    return yup.object().shape({
      insurerPartnerNumber: yup.mixed().required(),
      activationDays: yup.string().nullable(),
      suspensionDays: yup.string().nullable(),
      garageRiskDiscountPercentHull: yup.string().nullable(),
      garageRiskDiscountPercentHullBasic: yup.string().nullable(),
      paymentMethod: yup.mixed(),
      paymentMethodFleetLegal: yup.mixed(),
      paymentMethodHullOnLand: yup.mixed(),
      paymentMethodMachineryBreakdown: yup.mixed(),

      paymentMethodEngineTax: yup.mixed(),
      paymentMean: yup.mixed(),
      paymentMeanFleetLegal: yup.mixed(),
      paymentMeanHullOnLand: yup.mixed(),
      paymentMeanMachineryBreakdown: yup.mixed(),
      premiumTypeCode: yup.mixed().nullable(),
    });
  }, []);

  const onSubmit = useCallback(
    async (data) => {
      let dayMonthRenewalDate = null;

      if (data?.renewalDate) {
        dayMonthRenewalDate = formatDayMonthValueForPayload(data?.renewalDate);
      }
      const payload = {
        insurerPartnerNumber: Number(data?.insurerPartnerNumber?.value) || null,

        suspensionDays: formatNumberForPayload(data?.suspensionDays),
        activationDays: formatNumberForPayload(data?.activationDays),
        garageRiskDiscountPercentHull: formatNumberForPayload(
          stringToNumber(data?.garageRiskDiscountPercentHull)
        ),

        garageRiskDiscountPercentHullBasic: formatNumberForPayload(
          stringToNumber(data?.garageRiskDiscountPercentHullBasic)
        ),

        insurerBranchOffice: Number(data?.insurerBranchOffice?.value) || null,

        paymentMethod: Number(data?.paymentMethod?.value) || null,
        paymentMethodFleetLegal:
          Number(data?.paymentMethodFleetLegal?.value) || null,
        paymentMethodHullOnLand:
          Number(data?.paymentMethodHullOnLand?.value) || null,
        paymentMethodMachineryBreakdown:
          Number(data?.paymentMethodMachineryBreakdown?.value) || null,
        paymentMethodEngineTax:
          Number(data?.paymentMethodEngineTax?.value) || null,
        paymentMean: Number(data?.paymentMean?.value) || null,
        paymentMeanFleetLegal:
          Number(data?.paymentMeanFleetLegal?.value) || null,
        paymentMeanHullOnLand:
          Number(data?.paymentMeanHullOnLand?.value) || null,
        paymentMeanMachineryBreakdown:
          Number(data?.paymentMeanMachineryBreakdown?.value) || null,
        fleetInsurerDefaultSettingPerPremiumTypeDetails:
          suspensionDaysPremiumTypes.map((pt, i) => {
            const premiumTypeId = taxonomy?.PremiumType?.byCode[pt]?.id;
            const raw = data?.suspensionDaysLimit?.[i];
            const el: {
              premiumTypeCode: number;
              suspensionDaysLimit: number | null;
              id?: number;
            } = {
              premiumTypeCode: premiumTypeId,
              suspensionDaysLimit:
                raw === "" || raw === null || raw === undefined
                  ? null
                  : Number(raw),
            };
            if (isEditSetting) {
              const existing =
                settingItemData?.fleetInsurerDefaultSettingPerPremiumTypeDetails?.find(
                  (d) => d.premiumTypeCode === premiumTypeId
                );
              if (existing?.id) el.id = existing.id;
            }
            return el;
          }),
        ...(fleetAppSettings?.INCLUDE_INSURER_SETTING_PREMIUM_TYPE && {
          premiumTypeCode: Number(data?.premiumTypeCode?.value) || null,
        }),
        ...(fleetAppSettings?.INCLUDE_GROUP_POLICY && {
          insurerGroupPolicyNumber: data?.insurerGroupPolicyNumber
            ? data?.insurerGroupPolicyNumber.value
            : null,
          renewalDay:
            dayMonthRenewalDate?.dayField && dayMonthRenewalDate?.monthField
              ? dayMonthRenewalDate?.dayField
              : null,
          renewalMonth:
            dayMonthRenewalDate?.dayField && dayMonthRenewalDate?.monthField
              ? dayMonthRenewalDate?.monthField
              : null,
        }),
      };

      if (isCreateSetting || isCopySetting) {
        onCreateFleetInsurerDefaultSetting(
          {
            fleetId,
            data: payload,
          },
          {
            onSuccess: () => {
              toast.success(t("bfm.success"));
              onClose();
              dispatch(loadFleetWithInsurersActions.trigger({ fleetId }));
            },
          }
        );
      } else if (isEditSetting) {
        onUpdateFleetInsurerDefaultSetting(
          {
            fleetId,
            fleetInsurerSettingId: settingItemData.fleetInsurerDefaultSettingId,
            data: {
              ...payload,
              rowVersion: settingItemData?.rowVersion,
            },
          },
          {
            onSuccess: () => {
              toast.success(t("bfm.success"));
              onClose();
              dispatch(loadFleetWithInsurersActions.trigger({ fleetId }));
            },
          }
        );
      }
    },
    [fleetId, isCreateSetting, isEditSetting, settingItemData, taxonomy]
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={onSubmit}
    >
      {(props) => {
        const { values, handleSubmit, setFieldValue, handleChange, errors } =
          props;

        // Get current insurer group policy options based on selected insurer
        const currentInsurerGroupPolicyOptions =
          getInsurerGroupPolicyNumberOptions(
            values.insurerPartnerNumber?.value || null
          );

        const hasOneValueSelected = Object.keys(values)
          .filter(
            (field) =>
              field !== "insurerPartnerNumber" &&
              field !== "insurerBranchOffice" &&
              field !== "insurerGroupPolicyNumber" &&
              field !== "suspensionDaysLimit"
          )
          .some((field) => {
            return values[field];
          });

        return (
          <>
            <form onSubmit={handleSubmit}>
              <FormContainer>
                <ComboBoxField
                  required
                  disabled={isEditSetting || isReadOnly}
                  errors={errors}
                  name={"insurerPartnerNumber"}
                  label={"bfm.insurer.label"}
                  setFieldValue={setFieldValue}
                  options={insurerOptions || []}
                  defaultValue={initialValues?.insurerPartnerNumber}
                  confidentiality={null}
                  onChange={(selectedOption) => {
                    // When insurer changes, clear the group policy number
                    setFieldValue("insurerPartnerNumber", selectedOption);
                    setFieldValue("insurerGroupPolicyNumber", null);
                  }}
                />
                {fleetAppSettings?.INCLUDE_INSURER_SETTING_PREMIUM_TYPE && (
                  <ComboBoxField
                    disabled={isReadOnly}
                    errors={errors}
                    name={"premiumTypeCode"}
                    label={"bfm.fleet.premiumType.label"}
                    setFieldValue={setFieldValue}
                    options={insurerSettingPremiumTypeOptions || []}
                    confidentiality={null}
                    defaultValue={initialValues?.premiumTypeCode}
                  />
                )}
                {fleetAppSettings?.SUSPENSION_DAYS_INCLUDED && (
                  <NumberInputField
                    setFieldValue={setFieldValue}
                    value={values?.suspensionDays}
                    onChange={handleChange}
                    errors={errors}
                    name={"suspensionDays"}
                    disabled={isReadOnly}
                    //   isNegativeAllowed={true}
                    label={"bfm.suspensionDays"}
                  />
                )}
                {fleetAppSettings?.ACTIVATION_DAYS_INCLUDED && (
                  <NumberInputField
                    setFieldValue={setFieldValue}
                    value={values?.activationDays}
                    onChange={handleChange}
                    errors={errors}
                    name={"activationDays"}
                    disabled={isReadOnly}
                    //   isNegativeAllowed={true}
                    label={"bfm.activationDays"}
                  />
                )}
                {fleetAppSettings?.GARAGE_RISK_DISCOUNT_PERCENT_INCLUDED && (
                  <>
                    <DoubleNumberInputField
                      setFieldValue={setFieldValue}
                      value={values?.garageRiskDiscountPercentHull}
                      onChange={handleChange}
                      errors={errors}
                      name={"garageRiskDiscountPercentHull"}
                      disabled={isReadOnly}
                      //   isNegativeAllowed={true}
                      label={"bfm.garageRiskDiscountPercentHull.label"}
                    />
                    <DoubleNumberInputField
                      setFieldValue={setFieldValue}
                      value={values?.garageRiskDiscountPercentHullBasic}
                      onChange={handleChange}
                      errors={errors}
                      name={"garageRiskDiscountPercentHullBasic"}
                      disabled={isReadOnly}
                      //   isNegativeAllowed={true}
                      label={"bfm.garageRiskDiscountPercentHullBasic.label"}
                    />
                  </>
                )}
                <BranchOfficeField
                  defaultValue={initialValues?.insurerBranchOffice}
                  disabled={isReadOnly}
                />

                {fleetAppSettings?.INCLUDE_GROUP_POLICY ? (
                  <>
                    <GroupPolicyNumberField
                      id="insurerGroupPolicyNumber"
                      setFieldValue={setFieldValue}
                      options={currentInsurerGroupPolicyOptions || []}
                      defaultValue={values?.insurerGroupPolicyNumber}
                      errors={errors}
                      name={"insurerGroupPolicyNumber"}
                      disabled={isReadOnly}
                      label={"bfm.fleet.setting.insurerGroupPolicyNumber.label"}
                      confidentiality={null}
                      isClearable={true}
                    />
                    <TextInputField
                      isDayMonthField
                      onChange={handleChange}
                      value={values?.renewalDate}
                      errors={errors}
                      name={"renewalDate"}
                      disabled={false}
                      label={"bfm.fleetForm.renewalDate.label"}
                    />
                  </>
                ) : null}

                <ComboBoxField
                  disabled={isReadOnly}
                  errors={errors}
                  name={"paymentMethod"}
                  label={"bfm.vehicleForm.paymentMethod.label"}
                  setFieldValue={setFieldValue}
                  options={paymentMethodOptions || []}
                  confidentiality={null}
                  defaultValue={initialValues?.paymentMethod}
                />

                {fleetAppSettings?.VEHICLE_PREMIUM_TYPES.includes(
                  PremiumType.HORSE_POWER
                ) && (
                  <ComboBoxField
                    disabled={isReadOnly}
                    errors={errors}
                    name={"paymentMethodEngineTax"}
                    label={"bfm.vehicleForm.paymentMethodEngineTax.label"}
                    setFieldValue={setFieldValue}
                    options={paymentMethodOptions || []}
                    confidentiality={null}
                    defaultValue={initialValues?.paymentMethodEngineTax}
                  />
                )}
                {fleetAppSettings?.VEHICLE_PREMIUM_TYPES.includes(
                  PremiumType.FLEETLEGAL_PRORATA
                ) && (
                  <ComboBoxField
                    disabled={isReadOnly}
                    errors={errors}
                    name={"paymentMethodFleetLegal"}
                    label={"bfm.vehicleForm.paymentMethodFleetLegal.label"}
                    setFieldValue={setFieldValue}
                    options={paymentMethodOptions || []}
                    confidentiality={null}
                    defaultValue={initialValues?.paymentMethodFleetLegal}
                  />
                )}
                {fleetAppSettings?.VEHICLE_PREMIUM_TYPES.includes(
                  PremiumType.HULL_ON_LAND_PRORATA
                ) && (
                  <ComboBoxField
                    disabled={isReadOnly}
                    errors={errors}
                    name={"paymentMethodHullOnLand"}
                    label={"bfm.vehicleForm.paymentMethodHullOnLand.label"}
                    setFieldValue={setFieldValue}
                    options={paymentMethodOptions || []}
                    confidentiality={null}
                    defaultValue={initialValues?.paymentMethodHullOnLand}
                  />
                )}
                {fleetAppSettings?.VEHICLE_PREMIUM_TYPES.includes(
                  PremiumType.MACHINERY_BREAKDOWN_PRORATA
                ) && (
                  <ComboBoxField
                    disabled={isReadOnly}
                    errors={errors}
                    name={"paymentMethodMachineryBreakdown"}
                    label={
                      "bfm.vehicleForm.paymentMethodMachineryBreakdown.label"
                    }
                    setFieldValue={setFieldValue}
                    options={paymentMethodOptions || []}
                    confidentiality={null}
                    defaultValue={
                      initialValues?.paymentMethodMachineryBreakdown
                    }
                  />
                )}
                <ComboBoxField
                  disabled={isReadOnly}
                  errors={errors}
                  name={"paymentMean"}
                  label={"bfm.vehicleForm.paymentMean.label"}
                  setFieldValue={setFieldValue}
                  options={paymentMeanOptions || []}
                  confidentiality={null}
                  defaultValue={initialValues?.paymentMean}
                />
                {fleetAppSettings?.VEHICLE_PREMIUM_TYPES.includes(
                  PremiumType.FLEETLEGAL_PRORATA
                ) && (
                  <ComboBoxField
                    disabled={isReadOnly}
                    errors={errors}
                    name={"paymentMeanFleetLegal"}
                    label={"bfm.vehicleForm.paymentMeanFleetLegal.label"}
                    setFieldValue={setFieldValue}
                    options={paymentMeanOptions || []}
                    confidentiality={null}
                    defaultValue={initialValues?.paymentMeanFleetLegal}
                  />
                )}
                {fleetAppSettings?.VEHICLE_PREMIUM_TYPES.includes(
                  PremiumType.HULL_ON_LAND_PRORATA
                ) && (
                  <ComboBoxField
                    disabled={isReadOnly}
                    errors={errors}
                    name={"paymentMeanHullOnLand"}
                    label={"bfm.vehicleForm.paymentMeanHullOnLand.label"}
                    setFieldValue={setFieldValue}
                    options={paymentMeanOptions || []}
                    confidentiality={null}
                    defaultValue={initialValues?.paymentMeanHullOnLand}
                  />
                )}
                {fleetAppSettings?.VEHICLE_PREMIUM_TYPES.includes(
                  PremiumType.MACHINERY_BREAKDOWN_PRORATA
                ) && (
                  <ComboBoxField
                    disabled={isReadOnly}
                    errors={errors}
                    name={"paymentMeanMachineryBreakdown"}
                    label={
                      "bfm.vehicleForm.paymentMeanMachineryBreakdown.label"
                    }
                    setFieldValue={setFieldValue}
                    options={paymentMeanOptions || []}
                    confidentiality={null}
                    defaultValue={initialValues?.paymentMeanMachineryBreakdown}
                  />
                )}
                {fleetAppSettings?.SUSPENSION_DAYS_INCLUDED && (
                  <SuspensionLimitsSection>
                    <SectionLabel>
                      {t("bfm.vehicleForm.suspensionDaysLimit.label")}
                    </SectionLabel>
                    <SuspensionGrid>
                      {suspensionDaysPremiumTypes.map((pt, i) => (
                        <CellWrap key={pt}>
                          <CellLabel>{t(pt)}</CellLabel>
                          <NumberInputField
                            setFieldValue={setFieldValue}
                            value={values?.suspensionDaysLimit?.[i]}
                            onChange={handleChange}
                            errors={errors}
                            name={`suspensionDaysLimit[${i}]`}
                            disabled={isReadOnly}
                            label={""}
                          />
                        </CellWrap>
                      ))}
                    </SuspensionGrid>
                  </SuspensionLimitsSection>
                )}
              </FormContainer>

              <DialogFooter>
                <ActionsWrap>
                  <DefaultButton text={t("greco.cancel")} onClick={onClose} />
                  {!isReadOnly && (
                    <PrimaryButton
                      type="submit"
                      disabled={!hasOneValueSelected || createSettingInProgress}
                      text={t("greco.save")}
                    />
                  )}
                </ActionsWrap>
              </DialogFooter>
            </form>
          </>
        );
      }}
    </Formik>
  );
}

export default InsurerSettingForm;

const FormContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 10px;
`;

const ActionsWrap = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const SuspensionLimitsSection = styled.div`
  grid-column: 1 / -1;
  margin-top: 10px;
`;

const SectionLabel = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
`;

const SuspensionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-gap: 10px;
`;

const CellWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const CellLabel = styled.div`
  font-size: 12px;
  margin-bottom: 2px;
`;
